import type { AutonomyEvent } from '../api/types';

export type LogEntry = AutonomyEvent & { t: number; id: number };
export interface Cut {
  /** text actually spoken (prefix of the bubble); null = whole bubble spoken or unknown, just tag it */
  spoken: string | null;
  /** raw `spokenSoFar` from the event (may span several bubbles of the turn) */
  soFar: string | null;
  /** the bubble was spoken to the end (spokenSoFar ends with the whole text) */
  full: boolean;
}

/**
 * Barge-in: each `interrupted {turnId, spokenSoFar}` marks the latest robot bubble of that turn as cut off.
 * `spokenSoFar` may cover several bubbles of the turn, so we look for the longest prefix of the bubble it ends with.
 */
export function findCuts(log: LogEntry[]) {
  const cuts = new Map<number, Cut>();
  const orphans = new Set<number>();
  log.forEach((ev, idx) => {
    if (ev.kind !== 'interrupted') return;
    let target: LogEntry | undefined;
    for (let i = idx - 1; i >= 0; i--) {
      const e = log[i]!;
      if (e.kind !== 'reply' || e.spoken === false || !e.text) continue;
      if (ev.turnId != null && e.turnId != null && e.turnId !== ev.turnId) continue;
      target = e;
      break;
    }
    if (!target) return orphans.add(ev.id);
    const text = String(target.text);
    const so = typeof ev.spokenSoFar === 'string' ? ev.spokenSoFar.trimEnd() : null;
    let spoken: string | null = null;
    let full = false;
    if (so === '') spoken = '';
    else if (so)
      for (let k = Math.min(text.length, so.length); k > 0; k--)
        if (so.endsWith(text.slice(0, k).trimEnd())) {
          full = k >= text.length;
          spoken = full ? null : text.slice(0, k);
          break;
        }
    cuts.set(target.id, { spoken, soFar: so, full });
  });
  return { cuts, orphans };
}

/** Which log entries the transcript shows (state lines never; tool calls only when asked; interrupted only when unmatched). */
export function visibleEntries(log: LogEntry[], showTools: boolean) {
  const { cuts, orphans } = findCuts(log);
  return { cuts, shown: log.filter((e) => e.kind !== 'state' && (showTools || e.kind !== 'toolCall') && (e.kind !== 'interrupted' || orphans.has(e.id))) };
}
